/**
 * Small visual primitives for the Sales popout, built on the same shapes Client
 * View already uses: `DaysBars`' label / track / value grid and `SeverityBar`'s
 * proportional segments.
 */
import { TIER_COLOR } from './salesViewTokens';

/**
 * Where this client sits against its peer cohort, on a p25-p75 rail.
 *
 * A bare "38th percentile" is a number a rep has to translate. Drawing the
 * interquartile box and marking both the client and the median makes "behind the
 * pack" or "in the pack" readable without arithmetic.
 */
export function PercentileRail({ percentile, tone }: {
  percentile: number | null | undefined;
  tone?: 'good' | 'warn' | 'bad';
}) {
  if (percentile == null) return <span className="sv-rail-na">—</span>;
  const pct = Math.max(0, Math.min(1, percentile)) * 100;
  const band = tone ?? (pct >= 60 ? 'good' : pct >= 30 ? 'warn' : 'bad');
  return (
    <div className="sv-rail" title={`${Math.round(pct)}th percentile of the peer cohort`}>
      <div className="sv-rail-track">
        <span className="sv-rail-iqr" />
        <span className="sv-rail-median" />
        <span className={`sv-rail-dot sv-rail-dot--${band}`} style={{ left: `${pct}%` }} />
      </div>
      <span className={`sv-rail-value sv-rail-value--${band}`}>{Math.round(pct)}th</span>
    </div>
  );
}

/** Proportional share bar, e.g. live vs timed. Segments sum to the whole. */
export function ShareBar({ segments }: {
  segments: { key: string; label: string; value: number | null | undefined; color: string }[];
}) {
  const usable = segments.filter((s) => s.value != null && s.value > 0);
  const total = usable.reduce((sum, s) => sum + (s.value as number), 0);
  if (total <= 0) return <div className="sv-popout-empty">No mix data for this period.</div>;
  return (
    <div className="sv-sharebar-wrap">
      <div className="sv-sharebar">
        {usable.map((s) => (
          <span
            key={s.key}
            className="sv-sharebar-seg"
            style={{ width: `${((s.value as number) / total) * 100}%`, background: s.color }}
            title={`${s.label} — ${(((s.value as number) / total) * 100).toFixed(1)}%`}
          />
        ))}
      </div>
      <div className="sv-sharebar-legend">
        {usable.map((s) => (
          <span key={s.key} className="sv-sharebar-key">
            <span className="sv-sharebar-dot" style={{ background: s.color }} />
            {s.label} {(((s.value as number) / total) * 100).toFixed(0)}%
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Composition of a count, worst-first, with the counts spelled out.
 *
 * Used for the aged-units split, where the whole point is that the headline
 * number is almost entirely one component.
 */
export function CompositionBar({ parts, total }: {
  parts: { key: string; label: string; value: number; color: string; hint?: string }[];
  total: number;
}) {
  if (total <= 0) return null;
  const shown = parts.filter((p) => p.value > 0);
  return (
    <div className="sv-sharebar-wrap">
      <div className="sv-sharebar">
        {shown.map((p) => (
          <span
            key={p.key}
            className="sv-sharebar-seg"
            style={{ width: `${(p.value / total) * 100}%`, background: p.color }}
            title={p.hint ?? `${p.label} — ${p.value.toLocaleString()}`}
          />
        ))}
      </div>
      <div className="sv-sharebar-legend">
        {shown.map((p) => (
          <span key={p.key} className="sv-sharebar-key" title={p.hint}>
            <span className="sv-sharebar-dot" style={{ background: p.color }} />
            {p.value.toLocaleString()} {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Weekly volume sparkline: prior 8wk avg, 4wk avg, last week, this week. */
export function VolumeBars({ bars }: {
  bars: { key: string; label: string; value: number | null | undefined; hint?: string; muted?: boolean }[];
}) {
  const present = bars.filter((b) => b.value != null);
  if (present.length === 0) return <div className="sv-popout-empty">No volume history.</div>;
  const max = Math.max(...present.map((b) => b.value as number), 1);
  return (
    <div className="sv-volbars">
      {present.map((b) => (
        <div className="sv-volbar-row" key={b.key}>
          <span className="sv-volbar-label" title={b.hint}>{b.label}</span>
          <div className="sv-volbar-track">
            <div
              className={`sv-volbar-fill${b.muted ? ' sv-volbar-fill--muted' : ''}`}
              style={{ width: `${((b.value as number) / max) * 100}%`, background: b.muted ? undefined : TIER_COLOR.MEDIUM.pin }}
            />
          </div>
          <span className="sv-volbar-value">{(b.value as number).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span>
        </div>
      ))}
    </div>
  );
}
